import LoadFailure from '../../../components/Brand/LoadFailure';
import usePageLayout from '../../../hooks/usePageLayout';
import '../UserResponsive.scss';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Alert } from 'antd';
import BrandLoader from '../../../components/Brand/BrandLoader';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import ButtonPanelReaction from '../../../components/Buttons/ButtonPanelReaction';
import WiwHeaderTitle from '../../../components/WiwHeaderTitle';
import PostUser from '../../../components/PostUser';
import ButtonSubscribe from '../../../components/Buttons/ButtonSubscribe';
import ConfirmationPanel from '../../../components/ConfirmationPanel';
import { createName, getUser } from '../../../utils/func';
import api from '../../../services';
import { APP_ROUTES } from '../../../utils/constant';
import url from '../../../utils/url';
import './index.scss';

const { Header, Content, Footer } = Layout;
const sizePage = 20;
const emptyPage = { user: null, follow: [], loading: true, loadingMore: false, error: null, hasMore: true, lastId: null };
const uniqueUsers = (users) => [...new Map(users.map(user => [user.id, user])).values()];
const personName = person => createName(person).trim() || `@${person.user_name}`;

export default function UserFollower({ type }) {
  const { username } = useParams();
  const viewerId = getUser()?.id;
  const key = `${username}:${type}:${viewerId || 'guest'}`;
  const [attempt, setAttempt] = useState(0);
  const [confirmation, setConfirmation] = useState(null);
  const [actionError, setActionError] = useState(false);
  const [result, setResult] = useState({ ...emptyPage, key: null });
  const current = result.key === key ? result : emptyPage;
  const generation = useRef(0);
  const readController = useRef(null);
  // A ref closes the interval before React has rendered loadingMore=true.
  const inFlight = useRef(false);
  const confirming = useRef(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const footerRef = useRef(null);
  const contentRef = useRef(null);
  const { contentHeight, contentTop } = usePageLayout(headerRef, footerRef);
  const title = type === 'followers' ? t('user.followers') : t('user.following');
  const name = current.user ? `${createName(current.user)} @${current.user.user_name}` : '';

  useEffect(() => {
    const request = ++generation.current;
    const controller = new AbortController();
    readController.current = controller;
    inFlight.current = true;
    setResult({ ...emptyPage, key });
    setConfirmation(null);
    confirming.current = false;
    setActionError(false);
    if (contentRef.current) contentRef.current.scrollTop = 0;
    const load = async () => {
      try {
        const profile = await api.userByUsername({ username }, { signal: controller.signal });
        if (generation.current !== request) return;
        if (profile?.statusCode === 404 || (profile?.success && !profile.user)) {
          navigate(url(APP_ROUTES.PAGE404), { replace: true });
          return;
        }
        if (!profile?.success) throw new Error('Profile unavailable');
        const response = await api.userFollowers({ user_id: profile.user.id, type, size: sizePage, last_id: null }, { signal: controller.signal });
        if (generation.current !== request) return;
        if (!response?.success || !Array.isArray(response.list)) throw new Error('List unavailable');
        const list = response.list;
        setResult({ key, user: profile.user, follow: uniqueUsers(list), loading: false, loadingMore: false, error: null,
          lastId: list.length ? list[list.length - 1].id : null, hasMore: list.length >= sizePage });
      } catch {
        if (generation.current === request) setResult(previous => ({ ...previous, error: 'initial' }));
      } finally {
        if (generation.current === request) {
          inFlight.current = false;
          setResult(previous => ({ ...previous, loading: false }));
        }
      }
    };
    load();
    return () => {
      controller.abort();
      if (generation.current === request) {
        generation.current += 1;
        inFlight.current = false;
      }
    };
  }, [username, type, key, attempt, navigate]);

  const loadMore = async () => {
    if (inFlight.current || current.loading || !current.hasMore || !current.user || current.lastId == null || result.key !== key) return;
    inFlight.current = true;
    const request = generation.current;
    const cursor = current.lastId;
    setResult(previous => ({ ...previous, loadingMore: true, error: null }));
    try {
      const response = await api.userFollowers({ user_id: current.user.id, type, size: sizePage, last_id: cursor }, { signal: readController.current?.signal });
      if (generation.current !== request) return;
      if (!response?.success || !Array.isArray(response.list)) throw new Error('List unavailable');
      const list = response.list;
      const nextCursor = list.length ? list[list.length - 1].id : cursor;
      setResult(previous => ({ ...previous, follow: uniqueUsers([...previous.follow, ...list]), lastId: nextCursor,
        hasMore: list.length >= sizePage && nextCursor !== cursor }));
    } catch {
      if (generation.current === request) setResult(previous => ({ ...previous, error: 'more' }));
    } finally {
      if (generation.current === request) {
        inFlight.current = false;
        setResult(previous => ({ ...previous, loadingMore: false }));
      }
    }
  };

  const handleScroll = (event) => {
    if (current.error) return;
    const element = event.currentTarget;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 100) loadMore();
  };

  const confirmUnfollow = async () => {
    if (!confirmation || confirming.current) return;
    confirming.current = true;
    const request = generation.current;
    setConfirmation(previous => ({ ...previous, busy: true, error: false }));
    const success = await confirmation.run();
    if (request !== generation.current) return;
    confirming.current = false;
    setConfirmation(success ? null : { ...confirmation, busy: false, error: true });
  };

  const updateFollow = (id, following) => {
    // Keep an unfollowed row until the list is reloaded: its plus allows undo.
    setResult(previous => previous.key !== key ? previous : ({ ...previous,
      follow: previous.follow.map(person => person.id === id ? { ...person, following } : person),
    }));
    setActionError(false);
  };

  return (
    <Layout className="app-layout user-page follow-page">
      <Helmet>
        <title>{name ? `${t('user.profile_title')} ${name}` : title}</title>
        <meta name="description" content={name ? t('user.profile_description', { user: name }) : title} />
      </Helmet>
      <Header className="header" ref={headerRef}><WiwHeaderTitle title={title} /></Header>
      <Content className="content-layot profile_page" style={{ height: contentHeight, top: contentTop, overflow: 'auto' }}
        ref={contentRef} onScroll={handleScroll}>
        <div className="people-list">
          {current.follow.map(person => <PostUser key={person.id} author={person}
            action={person.id !== viewerId && <ButtonSubscribe user_id={person.id}
              personName={personName(person)} initialIsFollowing={person.following}
              readOnlyFollowing={type === 'followers'}
              onRequestUnfollow={run => {
                setActionError(false);
                setConfirmation({ key, name: personName(person), run, busy: false, error: false });
              }}
              onFollowChange={following => updateFollow(person.id, following)}
              onFollowError={() => { if (!person.following) setActionError(true); }} />}
          />)}
          {current.loading && <BrandLoader label={t('brand:loading')} />}
          {current.loadingMore && <div style={{ textAlign: 'center', padding: 20 }}>
            <BrandLoader compact label={t('brand:loading')} />
          </div>}
          {current.error && <LoadFailure onRetry={() => current.error === 'more' ? loadMore() : setAttempt(value => value + 1)} />}
          {!current.loading && !current.error && current.follow.length === 0 && <p className="follow-page-empty">
            {t(type === 'followers' ? 'brand:followersEmpty' : 'brand:followingEmpty')}
          </p>}
        </div>
      </Content>
      <Footer ref={footerRef}>
        {actionError && <Alert className="follow-page-feedback" type="error" role="alert"
          message={t('brand:saveError')} closable onClose={() => setActionError(false)} />}
        {confirmation?.key === key && <ConfirmationPanel className="follow-page-confirmation" action="unfollow"
          question={<>{t('brand:unfollowQuestion', { name: confirmation.name })}
            {confirmation.error && <span className="follow-page-confirmation-error" role="alert">{t('brand:saveError')}</span>}</>}
          confirmLabel={t('brand:unfollowAction')} busy={confirmation.busy}
          onOK={confirmUnfollow} onCancel={() => setConfirmation(null)} />}
        <ButtonPanelReaction />
      </Footer>
    </Layout>
  );
}
